import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Dynamic meta tags for each page
 * Supports Open Graph, Twitter Cards, and Schema.org structured data
 */
const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  schema,
  canonical,
  noindex = false,
  keywords = [],
  publishedTime,
  modifiedTime,
}) => {
  const siteName = 'Bohol Trabaho';
  const siteUrl = 'https://www.boholtrabaho.com';
  const defaultImage = `${siteUrl}/og-default.png`;
  const defaultDescription = 'Find jobs and services in Bohol, Philippines. Connect with verified local employers and job seekers. #1 Jobs Platform for Boholanos.';

  const seoTitle = title ? `${title} | ${siteName}` : `${siteName} - #1 Jobs & Services Platform for Bohol`;
  const seoDescription = description || defaultDescription;
  const seoImage = image || defaultImage;
  const seoUrl = url ? `${siteUrl}${url}` : siteUrl;
  const seoCanonical = canonical ? `${siteUrl}${canonical}` : seoUrl;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={seoCanonical} />
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_PH" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      
      {/* Article specific (for blog posts or job postings) */}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      
      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * Generate JobPosting Schema for Google for Jobs
 * @see https://developers.google.com/search/docs/appearance/structured-data/job-posting
 */
export const generateJobPostingSchema = (job, employer) => {
  if (!job) return null;
  
  const siteUrl = 'https://www.boholtrabaho.com';
  const jobUrl = `${siteUrl}/jobs/${job.slug || job.id}`;
  
  // Format salary for schema
  const salaryMin = job.salary_min || 0;
  const salaryMax = job.salary_max || salaryMin;
  // Real column is salary_period (daily|monthly); keep salary_type as a legacy fallback
  const salaryType = job.salary_period || job.salary_type || 'monthly';
  
  // Map salary type to schema unit
  const unitTextMap = {
    'hourly': 'HOUR',
    'daily': 'DAY',
    'weekly': 'WEEK',
    'monthly': 'MONTH',
    'yearly': 'YEAR',
    'project': 'MONTH' // Default project-based to month
  };
  
  // Calculate posting expiration (30 days from posting if not specified)
  const postedDate = job.created_at ? new Date(job.created_at).toISOString() : new Date().toISOString();
  const expiresAt = job.expires_at 
    ? new Date(job.expires_at).toISOString() 
    : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
  
  // Build employment type array from the REAL columns: job.type ∈ {permanent,
  // seasonal, contract} and job.work_schedule ∈ {full_time, part_time}. (Legacy
  // job.work_type is kept only as a fallback.) Mirrors backend seo_render.employment_type.
  const employmentTypes = [];
  if (job.type === 'contract') employmentTypes.push('CONTRACTOR');
  else if (job.type === 'seasonal') employmentTypes.push('TEMPORARY');
  if (job.work_schedule === 'part_time') employmentTypes.push('PART_TIME');
  else if (job.work_schedule === 'full_time') employmentTypes.push('FULL_TIME');
  if (employmentTypes.length === 0 && job.work_type) {
    const typeMap = {
      'full-time': 'FULL_TIME',
      'full_time': 'FULL_TIME',
      'part-time': 'PART_TIME',
      'part_time': 'PART_TIME',
      'contract': 'CONTRACTOR',
      'temporary': 'TEMPORARY',
      'intern': 'INTERN',
      'volunteer': 'VOLUNTEER',
      'freelance': 'CONTRACTOR'
    };
    const mappedType = typeMap[job.work_type?.toLowerCase()];
    if (mappedType) employmentTypes.push(mappedType);
  }
  if (employmentTypes.length === 0) {
    employmentTypes.push('OTHER'); // Default
  }
  
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    'title': job.title,
    'description': job.description || `${job.title} position available in ${job.location}, Bohol.`,
    'identifier': {
      '@type': 'PropertyValue',
      'name': 'Bohol Trabaho',
      'value': job.id
    },
    'datePosted': postedDate,
    'validThrough': expiresAt,
    'employmentType': employmentTypes,
    'hiringOrganization': {
      '@type': 'Organization',
      'name': employer?.business_name || employer?.name || 'Employer in Bohol',
      'sameAs': siteUrl,
      'logo': employer?.avatar_url || `${siteUrl}/logo-transparent.png`
    },
    'jobLocation': {
      '@type': 'Place',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': job.location || 'Bohol',
        'addressRegion': 'Bohol',
        'addressCountry': 'PH'
      }
    },
    'directApply': true,
    'url': jobUrl
  };

  // Only include salary when we actually have a positive figure — Google flags a
  // zero/empty baseSalary as an invalid JobPosting.
  if (salaryMin > 0) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      'currency': 'PHP',
      'value': {
        '@type': 'QuantitativeValue',
        'minValue': salaryMin,
        'maxValue': salaryMax,
        'unitText': unitTextMap[salaryType] || 'MONTH'
      }
    };
  }

  // Add benefits if available
  if (job.benefits && job.benefits.length > 0) {
    schema.jobBenefits = job.benefits.join(', ');
  }
  
  // Add skills/qualifications if available
  if (job.skills && job.skills.length > 0) {
    schema.skills = job.skills.join(', ');
  }
  
  return schema;
};

/**
 * Generate LocalBusiness Schema for employer profiles
 */
export const generateLocalBusinessSchema = (employer) => {
  if (!employer) return null;
  
  const siteUrl = 'https://www.boholtrabaho.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': employer.business_name || employer.name,
    'description': employer.business_description || `Business in ${employer.town || 'Bohol'}`,
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': employer.town || 'Bohol',
      'addressRegion': 'Bohol',
      'addressCountry': 'PH'
    },
    'url': `${siteUrl}/profile/${employer.id}`,
    ...(employer.avatar_url && { 'image': employer.avatar_url }),
    ...(employer.rating && { 
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': employer.rating,
        'bestRating': '5',
        'worstRating': '1'
      }
    })
  };
};

/**
 * Generate BreadcrumbList Schema
 */
export const generateBreadcrumbSchema = (items) => {
  const siteUrl = 'https://www.boholtrabaho.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url ? `${siteUrl}${item.url}` : undefined
    }))
  };
};

/**
 * Generate WebSite Schema with SearchAction for sitelinks search box
 */
export const generateWebsiteSchema = () => {
  const siteUrl = 'https://www.boholtrabaho.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'Bohol Trabaho',
    'alternateName': ['BoholTrabaho', 'Bohol Jobs', 'Trabaho sa Bohol'],
    'url': siteUrl,
    'description': 'Find jobs and services in Bohol, Philippines. Connect with verified local employers and job seekers.',
    'potentialAction': {
      '@type': 'SearchAction',
      'target': {
        '@type': 'EntryPoint',
        'urlTemplate': `${siteUrl}/jobs?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
};

/**
 * Generate Organization Schema
 */
export const generateOrganizationSchema = () => {
  const siteUrl = 'https://www.boholtrabaho.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'Bohol Trabaho',
    'url': siteUrl,
    'logo': `${siteUrl}/logo-transparent.png`,
    'description': 'The #1 Jobs & Services Platform for Bohol, Philippines. Connecting verified Boholano employers with local talent.',
    'foundingDate': '2024',
    'areaServed': {
      '@type': 'Place',
      'name': 'Bohol, Philippines'
    },
    'sameAs': [
      'https://www.facebook.com/boholtrabaho'
    ]
  };
};

export default SEO;
